import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Box, Button, Grid, Tooltip } from '@mui/material'
import { Add } from '@mui/icons-material'

import PageHeading from 'components/atoms/PageHeading'
import MainTable from 'components/organisms/tables/MainTable'
import { useTableModel } from 'utils/tableModels/useSetupEntityTableModel'
import { useHighlightsTableModel } from 'utils/tableModels/useHighlightsTableModel'
import { useFluenciesTableModel } from 'utils/tableModels/useFluenciesTableModel'

import { 
  useGET_schoolSetupAssessmentStrategiesQuery, 
  useGET_schoolSetupConcernReasonsQuery, 
  useGET_schoolSetupFluenciesQuery, 
  useGET_schoolSetupHighlightsQuery, 
  useGET_schoolSetupInstructionalStrategiesQuery, 
  useGET_schoolSetupInterventionStrategiesQuery, 
  useGET_schoolSetupInterventionTypesQuery, 
  useGET_schoolSetupLessonPlanOptionsQuery, 
  useGET_schoolSetupMeetingCategoriesQuery, 
  useGET_schoolSetupMonitoringMethodsQuery, 
  useGET_schoolSetupPlopCategoriesQuery,
  useGET_schoolSetupProblemAreasQuery,
  useGET_schoolSetupProgressMonitoringToolsQuery,
  useGET_schoolSetupSubjectsQuery,
  useGET_schoolSetupTopicsQuery
} from 'redux/api/setupApi'
import SetupConstantCrudPage from './crud/SetupConstantCrudPage'

const SetupEntityPage = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const params = useParams()
  const type = params?.type

  const map = {
    'assessment-strategies': {
      type: 'setting',
      model: null,
      canAdd: true,
      qry: useGET_schoolSetupAssessmentStrategiesQuery,
      title: t('common.assessment-strategies')
    },
    'concern-reasons': {
      type: 'setting',
      model: null,
      canAdd: true,
      qry: useGET_schoolSetupConcernReasonsQuery,
      title: t('common.concern-reasons')
    },
    'lesson-plan-options': {
      type: 'entity',
      model: null,
      canAdd: true,
      qry: useGET_schoolSetupLessonPlanOptionsQuery,
      title: t('common.lesson-plan-options')
    },
    'instructional-strategies': {
      type: 'setting',
      model: null,
      canAdd: true,
      qry: useGET_schoolSetupInstructionalStrategiesQuery,
      title: t('common.instructional-strategies')
    },
    'intervention-strategies': {
      type: 'setting',
      model: null,
      canAdd: true,
      qry: useGET_schoolSetupInterventionStrategiesQuery,
      title: t('common.intervention-strategies')
    },
    'intervention-types': {
      model: null,
      canAdd: true,
      qry: useGET_schoolSetupInterventionTypesQuery,
      title: t('common.intervention-types')
    },
    'plop-categories': {
      type: 'entity',
      model: null,
      canAdd: true,
      qry: useGET_schoolSetupPlopCategoriesQuery,
      title: t('common.plop-categories')
    },
    'problem-areas': {
      type: 'setting',
      model: null,
      canAdd: true,
      qry: useGET_schoolSetupProblemAreasQuery,
      title: t('common.problem-areas')
    },
    'progress-monitoring-tools': {
      type: 'setting',
      model: null,
      canAdd: true,
      qry: useGET_schoolSetupProgressMonitoringToolsQuery,
      title: t('common.progress-monitoring-tools')
    },
    'monitoring-methods': {
      type: 'setting',
      model: null,
      canAdd: true,
      qry: useGET_schoolSetupMonitoringMethodsQuery,
      title: t('common.monitoring-methods')
    },
    'subjects': {
      type: 'setting',
      model: null,
      canAdd: false,
      qry: useGET_schoolSetupSubjectsQuery,
      title: t('common.subjects')
    },
    'topics': {
      type: 'entity',
      model: null,
      canAdd: true,
      qry: useGET_schoolSetupTopicsQuery,
      title: t('common.topics')
    },
    'meeting-categories': {
      type: 'entity',
      model: null,
      canAdd: true,
      qry: useGET_schoolSetupMeetingCategoriesQuery,
      title: t('common.meeting-categories')
    },
    'fluencies': {
      type: 'entity',
      model: useFluenciesTableModel,
      canAdd: true,
      qry: useGET_schoolSetupFluenciesQuery,
      title: t('common.fluencies')
    },
    'highlights': {
      type: 'entity',
      model: useHighlightsTableModel,
      canAdd: true,
      qry: useGET_schoolSetupHighlightsQuery,
      title: t('common.highlights')
    },
    'assessment-types': {
      type: 'constant',
      title: t('common.assessment-types')
    },
    'submission-types': {
      type: 'constant',
      title: t('common.submission-types')
    },
    'score-types': {
      type: 'constant',
      title: t('common.score-types')
    },
  }

  return (
    map[type] &&
    <>
      <Box sx={{ margin: '1rem', marginBottom: '10px'  }}>
        <Grid container sx={{ alignItems: 'end' }}>
            <Grid item xs={9}>
              <PageHeading title={map[type].title} /> 
            </Grid>
            {!map[type].isConstant && map[type].canAdd &&
              <Grid item xs={3} style={{ textAlign: 'right' }}>
                <Tooltip title={`${t('common.add')}`} arrow placement='top'>
                  <Button
                    variant='contained'
                    color='success'
                    aria-haspopup='true'
                    onClick={() => navigate('add')}
                    disableElevation
                    sx={{ 
                      lineHeight: '1rem'
                    }}
                  >
                    <Add />
                    <span>{t('common.add')}</span>
                  </Button>
                </Tooltip>
              </Grid>
            }     
        </Grid>
      </Box>            

      {map[type].type === 'constant' && <SetupConstantCrudPage type={type} /> }

      {map[type].type === 'entity' &&
        <MainTable
          useTableModel={map[type].model || useTableModel}
          getTableData={map[type].qry}
          filters={"filters" in map[type] ? map[type]["filters"] : {}}
        />
      }
    </>
  )
}
export default SetupEntityPage
